import { Amplify } from "aws-amplify";
import { client, graphqlOperation } from "../libs/amplifyAPI";
import { AWS_CONFIGURATION } from './../config';
import listDoctorsFull from './../queries/listDoctorsFull';
import createDoctorMutation from './../mutations/createDoctor';
import updateDoctorMutation from './../mutations/updateDoctor';
import deleteDoctorMutation from './../mutations/deleteDoctor';
import updateDoctorAvailabilityMutation from './../mutations/updateDoctorAvailability';
import { formatRequest } from './../libs/general';

Amplify.configure(AWS_CONFIGURATION);

export const getDoctorsFull = async () => {
	try {
		// Pass the required isFavouriteOnly parameter as false to get all doctors
		const doctorsList = await client.graphql(graphqlOperation(listDoctorsFull, { isFavouriteOnly: false }));
		if (!doctorsList?.data?.listDoctors?.items) {
			console.error('Invalid response structure:', doctorsList);
			throw new Error('Invalid response from doctors service');
		}
		return doctorsList.data.listDoctors.items;
	} catch (e) {
		console.error('Error in getDoctorsFull:', e);
		throw new Error('Failed to get doctors list');
	}
}

export const createDoctor = async (params) => {
	if (params && params.mobile) params.mobile.countryPrefixCode = 356;
	if (params && params.docId) params.docId = params.docId.toLowerCase();
	if (params && params.email === '') delete params.email;
	try {
		const doctor = await client.graphql(graphqlOperation(createDoctorMutation, formatRequest(params)));

		if (doctor && doctor.errors && doctor.errors instanceof Array && doctor.errors.length >= 1)
			return new Error(doctor.errors);
		else if (doctor && doctor.data && doctor.data.createDoctor)
			return doctor.data.createDoctor;
		else return new Error('Failed to create doctor');
	} catch (e) {
		if (e instanceof Error) return e;
		else return new Error('Failed to create doctor');
	}
}

export const updateDoctor = async (params) => {
	try {
		// Validate required parameters
		if (!params || !params.docId) {
			console.error('Missing required docId in updateDoctor params:', params);
			return new Error('Doctor ID is required to update a doctor');
		}

		if (params.docId) params.docId = params.docId.toLowerCase();
		if (params.email === '') delete params.email;

		console.log('Updating doctor with params:', JSON.stringify(params, null, 2));
		
		const doctor = await client.graphql(graphqlOperation(updateDoctorMutation, formatRequest(params)));

		if (doctor && doctor.errors && doctor.errors instanceof Array && doctor.errors.length >= 1) {
			console.error('GraphQL errors in updateDoctor:', doctor.errors);
			return new Error(doctor.errors);
		} 
		else if (doctor && doctor.data && doctor.data.updateDoctor)
			return doctor.data.updateDoctor;
		else {
			console.error('Unexpected response format in updateDoctor:', doctor);
			return new Error('Failed to update doctor - unexpected response format');
		}
	} catch (e) {
		console.error('Exception in updateDoctor:', e);
		if (e instanceof Error) return e;
		else return new Error('Failed to update doctor');
	}
}

export const deleteDoctor = async (docId) => await client.graphql(graphqlOperation(deleteDoctorMutation, { docId }));

export const updateDoctorAvailability = async (docId, _from, to, type) => {
	try {
		 // Make sure docId is lowercase to match user IDs in Cognito
		if (docId) docId = docId.toLowerCase();
		
		console.log(`Preparing to update availability for doctor ${docId} with type: ${type}`);
		
		// Create parameters object with all required variables
		const params = {
			docId: docId,
			from: _from,
			to: to,
			type: type.toLowerCase() // Keep lowercase to match backend enum
		};
		
		console.log('GraphQL parameters:', params);
		
		// Make the GraphQL call using the imported mutation
		const result = await client.graphql(
			graphqlOperation(updateDoctorAvailabilityMutation, params)
		);
		
		console.log('GraphQL response:', result);

		// Handle potential errors and return appropriate response
		if (result?.errors?.length > 0) {
			console.error('GraphQL errors:', result.errors);
			const errorMessage = result.errors[0].message || 'Failed to update doctor\'s availability';
			return new Error(errorMessage);
		} 
		
		if (result?.data?.updateAvailability === null && !result.errors) {
			console.error('Authorization failure - null response without explicit errors');
			return new Error('Not authorized to update this doctor\'s availability');
		}
		
		return result.data?.updateAvailability || 'success';
	} catch (e) {
		console.error('Exception in updateDoctorAvailability:', e);
		return e instanceof Error ? e : new Error('Failed to update doctor\'s availability');
	}
}